import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { API_ROOT } from "../service/Services";

const MAIL_CONFIG_DEFAULTS = {
  gmail: "Gmail: max 100 emails per 15 min, 1s delay (recommended).",
  office365: "Office 365: max 200 per hour or 10,000 per day.",
  custom: "Custom SMTP: start with 50 per 15 min, 1.5s delay.",
};

const MAIL_SERVICE_PRESETS = [
  { label: "Gmail – 100 per 15 min (recommended)", maxPerWindow: 100, windowMinutes: 15, delayBetweenEmailsMs: 1000 },
  { label: "Gmail – 500 per day", maxPerWindow: 500, windowMinutes: 1440, delayBetweenEmailsMs: 2000 },
  { label: "Office 365 – 200 per hour", maxPerWindow: 200, windowMinutes: 60, delayBetweenEmailsMs: 500 },
  { label: "Office 365 – 10,000 per day", maxPerWindow: 10000, windowMinutes: 1440, delayBetweenEmailsMs: 0 },
  { label: "Custom – 50 per 15 min", maxPerWindow: 50, windowMinutes: 15, delayBetweenEmailsMs: 1500 },
];

const TEMPLATE_PLACEHOLDERS = "{{firstName}}, {{lastName}}, {{company}}, {{email}}, {{title}}, {{contact}}, {{name}} or {{this.name}}";

const CREDENTIAL_INSTRUCTIONS = {
  gmail: [
    "1. Use your Gmail address (e.g. you@gmail.com) as the Email.",
    "2. Gmail blocks normal passwords for apps. You need an App Password:",
    "   • Go to Google Account → Security → 2-Step Verification (enable it if needed).",
    "   • Then go to App passwords (or search 'App passwords' in Google Account).",
    "   • Select app: Mail, device: Other, name it (e.g. Lead Management), then Generate.",
    "   • Copy the 16-character password (no spaces) and paste it in the Password field here.",
    "3. Save the account and use Test to send a test email.",
  ],
  office365: [
    "1. Use your Office 365 / Outlook.com email (e.g. you@company.com or you@outlook.com) as the Email.",
    "2. Password: use your normal Microsoft account password, or an App password if you use 2FA:",
    "   • Go to Microsoft account → Security → Advanced security options.",
    "   • Create a new app password and use it in the Password field here.",
    "3. Save the account and use Test to send a test email.",
  ],
  custom: [
    "1. Enter the SMTP login (usually your email) and password provided by your email host.",
    "2. Host/port: e.g. smtp.example.com, port 587 (TLS) or 465 (SSL).",
    "3. Save and use Test to verify.",
  ],
};

export default function Mail({ section: sectionProp }) {
  const { hash, pathname } = useLocation();
  // Support separate routes: /mail/service-types, /mail/services, /mail/templates
  const sectionFromPath = pathname.split("/").pop();
  const section = sectionProp || (["service-types", "services", "templates"].includes(sectionFromPath) ? sectionFromPath : null);
  const authHeaders = useMemo(
    () => ({ headers: { authorization: `Bearer ${localStorage.getItem("token")}` } }),
    []
  );

  const [serviceTypes, setServiceTypes] = useState([]);
  const [mailServices, setMailServices] = useState([]);
  const [mailTemplates, setMailTemplates] = useState([]);

  const [showServiceTypeModal, setShowServiceTypeModal] = useState(false);
  const [editingServiceType, setEditingServiceType] = useState(null);
  const [serviceTypeForm, setServiceTypeForm] = useState({ name: "", description: "", order: 0 });

  const [showMailServiceModal, setShowMailServiceModal] = useState(false);
  const [editingMailService, setEditingMailService] = useState(null);
  const [mailServiceForm, setMailServiceForm] = useState({
    name: "",
    type: "gmail",
    maxPerWindow: 100,
    windowMinutes: 15,
    delayBetweenEmailsMs: 1000,
    isActive: true,
    order: 0,
    user: "",
    pass: "",
    host: "",
    port: 587,
    serviceTypeId: "",
  });
  const [showCredentialHelp, setShowCredentialHelp] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ name: "", subject: "", body: "", html: "", serviceTypeId: "" });

  const [showTestModal, setShowTestModal] = useState(false);
  const [testingAccount, setTestingAccount] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    fetchServiceTypes();
    fetchMailServices();
    fetchMailTemplates();
  }, []);

  const fetchServiceTypes = async () => {
    try {
      const res = await axios.get(`${API_ROOT}/mail-service-types`, authHeaders);
      if (res.data?.success) setServiceTypes(res.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (hash === "#services" || hash === "#templates" || hash === "#service-types") {
      const id = hash.slice(1);
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [hash]);

  const fetchMailServices = async () => {
    try {
      const res = await axios.get(`${API_ROOT}/mail-services`, authHeaders);
      if (res.data?.success) setMailServices(res.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchMailTemplates = async () => {
    try {
      const res = await axios.get(`${API_ROOT}/mail-templates`, authHeaders);
      if (res.data?.success) setMailTemplates(res.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const openAddServiceType = () => {
    setEditingServiceType(null);
    setServiceTypeForm({ name: "", description: "", order: serviceTypes.length });
    setShowServiceTypeModal(true);
  };

  const openEditServiceType = (st) => {
    setEditingServiceType(st);
    setServiceTypeForm({ name: st.name || "", description: st.description || "", order: st.order ?? 0 });
    setShowServiceTypeModal(true);
  };

  const saveServiceType = async () => {
    if (!serviceTypeForm.name.trim()) {
      alert("Service name is required");
      return;
    }
    try {
      if (editingServiceType) {
        await axios.put(`${API_ROOT}/mail-service-types/${editingServiceType._id}`, serviceTypeForm, authHeaders);
      } else {
        await axios.post(`${API_ROOT}/mail-service-types`, serviceTypeForm, authHeaders);
      }
      setShowServiceTypeModal(false);
      fetchServiceTypes();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to save");
    }
  };

  const deleteServiceType = async (id) => {
    if (!window.confirm("Delete this service type? Mail accounts and templates linked to it will be unlinked.")) return;
    try {
      await axios.delete(`${API_ROOT}/mail-service-types/${id}`, authHeaders);
      fetchServiceTypes();
      fetchMailServices();
      fetchMailTemplates();
    } catch (err) {
      alert(err.response?.data?.message || "Delete failed");
    }
  };

  const openAddMailService = () => {
    setEditingMailService(null);
    setMailServiceForm({
      name: "",
      type: "gmail",
      maxPerWindow: 100,
      windowMinutes: 15,
      delayBetweenEmailsMs: 1000,
      isActive: true,
      order: mailServices.length,
      user: "",
      pass: "",
      host: "",
      port: 587,
      serviceTypeId: "",
    });
    setShowCredentialHelp(null);
    setShowPassword(false);
    setShowMailServiceModal(true);
  };

  const openEditMailService = (s) => {
    setEditingMailService(s);
    setMailServiceForm({
      name: s.name || "",
      type: s.type || "gmail",
      maxPerWindow: s.maxPerWindow ?? 100,
      windowMinutes: s.windowMinutes ?? 15,
      delayBetweenEmailsMs: s.delayBetweenEmailsMs ?? 0,
      isActive: s.isActive !== false,
      order: s.order ?? 0,
      user: s.user || "",
      pass: "",
      host: s.host || "",
      port: s.port ?? 587,
      serviceTypeId: s.serviceTypeId?._id || s.serviceTypeId || "",
    });
    setShowCredentialHelp(null);
    setShowPassword(false);
    setShowMailServiceModal(true);
  };

  const saveMailService = async () => {
    const user = (mailServiceForm.user || "").trim();
    if (!user) {
      alert("Email / user is required");
      return;
    }
    if (!editingMailService && !(mailServiceForm.pass || "").trim()) {
      alert("Password is required when adding a new account");
      return;
    }
    try {
      const payload = {
        name: mailServiceForm.name.trim(),
        type: mailServiceForm.type,
        user,
        maxPerWindow: mailServiceForm.maxPerWindow,
        windowMinutes: mailServiceForm.windowMinutes,
        delayBetweenEmailsMs: mailServiceForm.delayBetweenEmailsMs || 0,
        isActive: mailServiceForm.isActive,
        order: mailServiceForm.order,
        serviceTypeId: mailServiceForm.serviceTypeId || null,
      };
      if ((mailServiceForm.pass || "").trim()) payload.pass = mailServiceForm.pass.trim();
      if (mailServiceForm.type === "custom") {
        payload.host = (mailServiceForm.host || "").trim();
        payload.port = mailServiceForm.port ?? 587;
      }
      if (editingMailService) {
        await axios.put(`${API_ROOT}/mail-services/${editingMailService._id}`, payload, authHeaders);
      } else {
        await axios.post(`${API_ROOT}/mail-services`, payload, authHeaders);
      }
      setShowMailServiceModal(false);
      fetchMailServices();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to save");
    }
  };

  const deleteMailService = async (id) => {
    if (!window.confirm("Delete this mail account?")) return;
    try {
      await axios.delete(`${API_ROOT}/mail-services/${id}`, authHeaders);
      fetchMailServices();
    } catch (err) {
      alert(err.response?.data?.message || "Delete failed");
    }
  };

  const openTestModal = (s) => {
    setTestingAccount(s);
    setTestEmail("");
    setShowTestModal(true);
  };

  const runTest = async () => {
    const email = testEmail.trim();
    if (!email) {
      alert("Enter an email address to send the test to.");
      return;
    }
    setTesting(true);
    try {
      const res = await axios.post(
        `${API_ROOT}/mail-services/${testingAccount._id}/test`,
        { testEmail: email },
        authHeaders
      );
      alert(res.data.message || "Test email sent. Check your inbox.");
      setShowTestModal(false);
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || err.message || "Test failed – account may not be working.";
      const isGmailBadCredentials =
        /535|BadCredentials|Username and Password not accepted/i.test(msg) &&
        (testingAccount?.type === "gmail" || /gmail|gsmtp/i.test(msg));
      if (isGmailBadCredentials) {
        const accountName = testingAccount?.name || "this account";
        const username = testingAccount?.user || "(check Username in the table)";
        alert(
          `Account: ${accountName}\nUsername: ${username}\nPassword: use App Password (see below)\n\n` +
            "Gmail requires an App Password, not your normal password.\n\n" +
            "1. Go to Google Account → Security → enable 2-Step Verification if needed.\n" +
            "2. Open 'App passwords' (search in Google Account).\n" +
            "3. Create one: App = Mail, Device = Other, name it (e.g. Lead Management).\n" +
            "4. Copy the 16-character password and paste it in the Password field when editing this mail account.\n\n" +
            "Help: https://support.google.com/mail/?p=BadCredentials"
        );
      } else {
        alert(msg);
      }
    } finally {
      setTesting(false);
    }
  };

  const openAddTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({ name: "", subject: "", body: "", html: "", serviceTypeId: "" });
    setShowTemplateModal(true);
  };

  const openEditTemplate = (t) => {
    setEditingTemplate(t);
    setTemplateForm({
      name: t.name || "",
      subject: t.subject || "",
      body: t.body ?? "",
      html: t.html ?? "",
      serviceTypeId: t.serviceTypeId?._id || t.serviceTypeId || "",
    });
    setShowTemplateModal(true);
  };

  const saveTemplate = async () => {
    if (!templateForm.name.trim()) {
      alert("Template name is required");
      return;
    }
    try {
      const payload = {
        name: templateForm.name.trim(),
        subject: templateForm.subject,
        body: templateForm.body,
        html: templateForm.html ?? "",
        serviceTypeId: templateForm.serviceTypeId || null,
      };
      if (editingTemplate) {
        await axios.put(`${API_ROOT}/mail-templates/${editingTemplate._id}`, payload, authHeaders);
      } else {
        await axios.post(`${API_ROOT}/mail-templates`, payload, authHeaders);
      }
      setShowTemplateModal(false);
      fetchMailTemplates();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to save template");
    }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await axios.delete(`${API_ROOT}/mail-templates/${id}`, authHeaders);
      fetchMailTemplates();
    } catch (err) {
      alert(err.response?.data?.message || "Delete failed");
    }
  };

  const showAll = !section;
  const showServiceTypes = showAll || section === "service-types";
  const showServices = showAll || section === "services";
  const showTemplates = showAll || section === "templates";

  return (
    <div className="container mx-auto mt-8 p-6 bg-white rounded-xl shadow-sm">
      <h2 className="text-2xl font-semibold text-gray-900 mb-6">Mail configuration</h2>

      {/* Mail config – suggested defaults (only on main /mail page) */}
      {showAll && (
      <section className="mb-8 rounded-lg border border-purple-200 bg-purple-50 p-4">
        <h3 className="text-lg font-semibold text-purple-900 mb-2">Suggested max mail (best options)</h3>
        <ul className="text-sm text-purple-800 space-y-1">
          <li>• {MAIL_CONFIG_DEFAULTS.gmail}</li>
          <li>• {MAIL_CONFIG_DEFAULTS.office365}</li>
          <li>• {MAIL_CONFIG_DEFAULTS.custom}</li>
        </ul>
      </section>
      )}

      {/* Service types (e.g. RPA) – full CRUD */}
      {showServiceTypes && (
      <section id="service-types" className="mb-8 scroll-mt-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Service types (e.g. RPA)</h3>
          <button
            type="button"
            onClick={openAddServiceType}
            className="px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700"
          >
            + Add service type
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-2">Create services like RPA; link mail accounts and templates to them.</p>
        {serviceTypes.length === 0 ? (
          <p className="text-gray-500">No service types. Add one (e.g. RPA) then link mail accounts and templates.</p>
        ) : (
          <div className="overflow-x-auto border rounded-lg">
            <table className="min-w-full text-sm border-collapse">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border px-3 py-2 text-left">Name</th>
                  <th className="border px-3 py-2 text-left">Description</th>
                  <th className="border px-3 py-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {serviceTypes.map((st) => (
                  <tr key={st._id} className="bg-white hover:bg-gray-50">
                    <td className="border px-3 py-2 font-medium">{st.name}</td>
                    <td className="border px-3 py-2 text-gray-600">{st.description || "—"}</td>
                    <td className="border px-3 py-2">
                      <button type="button" onClick={() => openEditServiceType(st)} className="text-blue-600 hover:underline mr-2">Edit</button>
                      <button type="button" onClick={() => deleteServiceType(st._id)} className="text-red-600 hover:underline">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      )}

      {/* Mail accounts (services) – full CRUD + Test */}
      {showServices && (
      <section id="services" className="mb-8 scroll-mt-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Mail accounts (services)</h3>
          <button
            type="button"
            onClick={openAddMailService}
            className="px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700"
          >
            + Add account
          </button>
        </div>
        <p className="text-sm text-gray-600 mb-2">Email and password are saved in the database (no .env needed). Use Add account to enter credentials, then Test to verify.</p>
        {mailServices.length === 0 ? (
          <p className="text-gray-500">No mail accounts. Add one to send campaigns from Sales Team Data.</p>
        ) : (
          <div className="overflow-x-auto border rounded-lg">
            <table className="min-w-full text-sm border-collapse">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border px-3 py-2 text-left">Name</th>
                  <th className="border px-3 py-2 text-left">Username (email)</th>
                  <th className="border px-3 py-2 text-left">Password</th>
                  <th className="border px-3 py-2 text-left">Service</th>
                  <th className="border px-3 py-2 text-left">Type</th>
                  <th className="border px-3 py-2 text-left">Rate limit</th>
                  <th className="border px-3 py-2 text-left">Active</th>
                  <th className="border px-3 py-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {mailServices.map((s) => (
                  <tr key={s._id} className="bg-white hover:bg-gray-50">
                    <td className="border px-3 py-2 font-medium">{s.name}</td>
                    <td className="border px-3 py-2 text-gray-700">{s.user || "—"}</td>
                    <td className="border px-3 py-2 text-gray-500">{s.user ? "••••••" : "—"}</td>
                    <td className="border px-3 py-2">{s.serviceTypeId?.name || "—"}</td>
                    <td className="border px-3 py-2">{s.type}</td>
                    <td className="border px-3 py-2">{s.maxPerWindow} / {s.windowMinutes} min</td>
                    <td className="border px-3 py-2">{s.isActive ? "Yes" : "No"}</td>
                    <td className="border px-3 py-2">
                      <button type="button" onClick={() => openTestModal(s)} className="text-emerald-600 hover:underline mr-2">Test</button>
                      <button type="button" onClick={() => openEditMailService(s)} className="text-blue-600 hover:underline mr-2">Edit</button>
                      <button type="button" onClick={() => deleteMailService(s._id)} className="text-red-600 hover:underline">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      )}

      {/* Templates – full CRUD */}
      {showTemplates && (
      <section id="templates" className="scroll-mt-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Templates</h3>
          <button
            type="button"
            onClick={openAddTemplate}
            className="px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700"
          >
            + Add template
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-2">Use placeholders: {TEMPLATE_PLACEHOLDERS}. Replaced per contact when sending from Sales Team Data.</p>
        {mailTemplates.length === 0 ? (
          <p className="text-gray-500">No templates. Add one to use in Send mail on Sales Team Data.</p>
        ) : (
          <div className="overflow-x-auto border rounded-lg">
            <table className="min-w-full text-sm border-collapse">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border px-3 py-2 text-left">Name</th>
                  <th className="border px-3 py-2 text-left">Service</th>
                  <th className="border px-3 py-2 text-left">Subject</th>
                  <th className="border px-3 py-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {mailTemplates.map((t) => (
                  <tr key={t._id} className="bg-white hover:bg-gray-50">
                    <td className="border px-3 py-2 font-medium">{t.name}</td>
                    <td className="border px-3 py-2">{t.serviceTypeId?.name || "—"}</td>
                    <td className="border px-3 py-2 truncate max-w-xs" title={t.subject}>{t.subject}</td>
                    <td className="border px-3 py-2">
                      <button type="button" onClick={() => openEditTemplate(t)} className="text-blue-600 hover:underline mr-2">Edit</button>
                      <button type="button" onClick={() => deleteTemplate(t._id)} className="text-red-600 hover:underline">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      )}

      {/* Service type add/edit modal */}
      {showServiceTypeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">{editingServiceType ? "Edit service type" : "Add service type"}</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input type="text" value={serviceTypeForm.name} onChange={(e) => setServiceTypeForm({ ...serviceTypeForm, name: e.target.value })} placeholder="e.g. RPA" className="w-full px-3 py-2 border rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input type="text" value={serviceTypeForm.description} onChange={(e) => setServiceTypeForm({ ...serviceTypeForm, description: e.target.value })} placeholder="Optional" className="w-full px-3 py-2 border rounded-md" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={() => setShowServiceTypeModal(false)} className="px-4 py-2 rounded bg-gray-100 hover:bg-gray-200">Cancel</button>
              <button type="button" onClick={saveServiceType} className="px-4 py-2 rounded bg-purple-600 text-white hover:bg-purple-700">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Mail service add/edit modal */}
      {showMailServiceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
            <h3 className="text-lg font-semibold mb-4">{editingMailService ? "Edit mail account" : "Add mail account"}</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Service type (e.g. RPA)</label>
                <select value={mailServiceForm.serviceTypeId || ""} onChange={(e) => setMailServiceForm({ ...mailServiceForm, serviceTypeId: e.target.value })} className="w-full px-3 py-2 border rounded-md">
                  <option value="">None</option>
                  {serviceTypes.map((st) => (
                    <option key={st._id} value={st._id}>{st.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input type="text" value={mailServiceForm.name} onChange={(e) => setMailServiceForm({ ...mailServiceForm, name: e.target.value })} placeholder="e.g. Gmail Main" className="w-full px-3 py-2 border rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={mailServiceForm.type}
                  onChange={(e) => {
                    const t = e.target.value;
                    setMailServiceForm({ ...mailServiceForm, type: t });
                    setShowCredentialHelp(null);
                  }}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="gmail">Gmail</option>
                  <option value="office365">Office 365</option>
                  <option value="custom">Custom SMTP</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {mailServiceForm.type === "custom" ? "SMTP user / Email" : "Email"}
                </label>
                <input
                  type="email"
                  value={mailServiceForm.user}
                  onChange={(e) => setMailServiceForm({ ...mailServiceForm, user: e.target.value })}
                  placeholder={mailServiceForm.type === "gmail" ? "you@gmail.com" : mailServiceForm.type === "office365" ? "you@company.com" : "user@example.com"}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {mailServiceForm.type === "custom" ? "SMTP password" : "Password (App password for Gmail)"}
                </label>
                <div className="flex gap-2 items-center">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={mailServiceForm.pass}
                    onChange={(e) => setMailServiceForm({ ...mailServiceForm, pass: e.target.value })}
                    placeholder={editingMailService ? "Leave blank to keep current" : "Required for new account"}
                    className="flex-1 px-3 py-2 border rounded-md"
                  />
                  <label className="flex items-center gap-1.5 text-sm text-gray-600 whitespace-nowrap cursor-pointer">
                    <input type="checkbox" checked={showPassword} onChange={(e) => setShowPassword(e.target.checked)} className="rounded" />
                    Show
                  </label>
                </div>
              </div>
              {mailServiceForm.type === "custom" && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Host</label>
                    <input type="text" value={mailServiceForm.host} onChange={(e) => setMailServiceForm({ ...mailServiceForm, host: e.target.value })} placeholder="smtp.example.com" className="w-full px-3 py-2 border rounded-md" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Port</label>
                    <input type="number" value={mailServiceForm.port} onChange={(e) => setMailServiceForm({ ...mailServiceForm, port: Number(e.target.value) || 587 })} className="w-full px-3 py-2 border rounded-md" />
                  </div>
                </div>
              )}
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <button
                  type="button"
                  onClick={() => setShowCredentialHelp(showCredentialHelp === mailServiceForm.type ? null : mailServiceForm.type)}
                  className="text-sm font-medium text-amber-800 hover:underline"
                >
                  {showCredentialHelp === mailServiceForm.type ? "▼ " : "▶ "} How to get Email & Password ({mailServiceForm.type === "gmail" ? "Gmail" : mailServiceForm.type === "office365" ? "Office 365" : "Custom"})
                </button>
                {showCredentialHelp === mailServiceForm.type && (
                  <ul className="mt-2 text-xs text-amber-900 space-y-1 list-disc list-inside">
                    {CREDENTIAL_INSTRUCTIONS[mailServiceForm.type].map((line, i) => (
                      <li key={i}>{line}</li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Preset</label>
                <select
                  className="w-full px-3 py-2 border rounded-md"
                  onChange={(e) => {
                    const idx = Number(e.target.value);
                    if (idx >= 0 && MAIL_SERVICE_PRESETS[idx]) {
                      const p = MAIL_SERVICE_PRESETS[idx];
                      setMailServiceForm((f) => ({ ...f, maxPerWindow: p.maxPerWindow, windowMinutes: p.windowMinutes, delayBetweenEmailsMs: p.delayBetweenEmailsMs }));
                    }
                  }}
                >
                  <option value="">Choose preset…</option>
                  {MAIL_SERVICE_PRESETS.map((p, idx) => (
                    <option key={idx} value={idx}>{p.label}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max per window</label>
                  <input type="number" min={1} value={mailServiceForm.maxPerWindow} onChange={(e) => setMailServiceForm({ ...mailServiceForm, maxPerWindow: Number(e.target.value) || 1 })} className="w-full px-3 py-2 border rounded-md" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Window (min)</label>
                  <input type="number" min={1} value={mailServiceForm.windowMinutes} onChange={(e) => setMailServiceForm({ ...mailServiceForm, windowMinutes: Number(e.target.value) || 1 })} className="w-full px-3 py-2 border rounded-md" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Delay (ms)</label>
                  <input type="number" min={0} value={mailServiceForm.delayBetweenEmailsMs} onChange={(e) => setMailServiceForm({ ...mailServiceForm, delayBetweenEmailsMs: Number(e.target.value) || 0 })} className="w-full px-3 py-2 border rounded-md" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="ms-active" checked={mailServiceForm.isActive} onChange={(e) => setMailServiceForm({ ...mailServiceForm, isActive: e.target.checked })} />
                <label htmlFor="ms-active">Active</label>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={() => setShowMailServiceModal(false)} className="px-4 py-2 rounded bg-gray-100 hover:bg-gray-200">Cancel</button>
              <button type="button" onClick={saveMailService} className="px-4 py-2 rounded bg-purple-600 text-white hover:bg-purple-700">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Template add/edit modal – body + html preserved exactly for format */}
      {showTemplateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <h3 className="text-lg font-semibold mb-4">{editingTemplate ? "Edit template" : "Add template"}</h3>
            <p className="text-xs text-gray-600 mb-3">Placeholders: {TEMPLATE_PLACEHOLDERS}</p>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Service type (e.g. RPA)</label>
                <select value={templateForm.serviceTypeId || ""} onChange={(e) => setTemplateForm({ ...templateForm, serviceTypeId: e.target.value })} className="w-full px-3 py-2 border rounded-md">
                  <option value="">None</option>
                  {serviceTypes.map((st) => (
                    <option key={st._id} value={st._id}>{st.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input type="text" value={templateForm.name} onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })} placeholder="e.g. Welcome email" className="w-full px-3 py-2 border rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <input type="text" value={templateForm.subject} onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })} placeholder="e.g. Hi {{firstName}}" className="w-full px-3 py-2 border rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Body (plain text)</label>
                <textarea value={templateForm.body} onChange={(e) => setTemplateForm({ ...templateForm, body: e.target.value })} placeholder="e.g. Dear {{name}}, ..." rows={5} className="w-full px-3 py-2 border rounded-md font-mono text-sm resize-y" spellCheck="false" />
                <p className="text-xs text-gray-500 mt-0.5">Plain text version. Stored and sent as-is.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">HTML (formatted)</label>
                <textarea value={templateForm.html} onChange={(e) => setTemplateForm({ ...templateForm, html: e.target.value })} placeholder="e.g. <p>Dear {{firstName}},</p><p>...</p>" rows={8} className="w-full px-3 py-2 border rounded-md font-mono text-sm resize-y" spellCheck="false" />
                <p className="text-xs text-gray-500 mt-0.5">HTML content for formatted emails. Loaded and saved exactly as entered.</p>
              </div>
              {templateForm.html?.trim() && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <p className="text-sm font-medium text-gray-700 mb-2">HTML preview</p>
                  <div className="prose prose-sm max-w-none bg-white border border-gray-200 rounded-md p-3" dangerouslySetInnerHTML={{ __html: templateForm.html }} />
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={() => setShowTemplateModal(false)} className="px-4 py-2 rounded bg-gray-100 hover:bg-gray-200">Cancel</button>
              <button type="button" onClick={saveTemplate} className="px-4 py-2 rounded bg-purple-600 text-white hover:bg-purple-700">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Test mail account modal */}
      {showTestModal && testingAccount && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-2">Test mail account: {testingAccount.name}</h3>
            <p className="text-sm text-gray-600 mb-4">Send a test email to check if this account is working.</p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Send test email to</label>
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowTestModal(false)} disabled={testing} className="px-4 py-2 rounded bg-gray-100 hover:bg-gray-200">Cancel</button>
              <button type="button" onClick={runTest} disabled={testing} className="px-4 py-2 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
                {testing ? "Sending…" : "Send test"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
